import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Image, SimpleGrid, VStack } from '@chakra-ui/react';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import { getCardRequestAssetSrc } from 'common/paymentCards/utils';
import SunshineCard, { SunshineCardProps } from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { TeamMemberRequestStatusBadge } from 'components/TeamMemberRequestStatusBadge';
import { TeamRequestStatusToRequestsFilterState } from 'features/Team/pages/Requests/utils';
import { formatDate } from 'helpers/date';

import * as routes from '../../../../../../routes';
import {
  isVirtualBudgetCardRequest,
  PaymentCardRequest,
} from '../../../../hooks/useRequests';
import locales from './locales';

interface PaymentCardRequestRowProps
  extends Pick<SunshineCardProps, 'isActive'> {
  isReviewer: boolean;
  request: PaymentCardRequest;
  onClick: () => void;
}

const PaymentCardRequestRow: FC<PaymentCardRequestRowProps> = ({
  isActive,
  isReviewer,
  onClick,
  request,
  ...props
}) => (
  <SunshineCard
    alignItems="center"
    as={Link}
    isActive={isActive}
    onClick={onClick}
    to={routes.getRequestPath(
      request.teamMembersRequestId,
      TeamRequestStatusToRequestsFilterState[request.status],
    )}
    {...props}
  >
    <SunshineCard.Slot name="aside">
      <Image
        alt=""
        boxSize="40px"
        objectFit="contain"
        src={getCardRequestAssetSrc(request)}
      />
    </SunshineCard.Slot>

    <SimpleGrid
      alignItems="center"
      columns={3}
      spacing="space-24"
      templateColumns="6fr 3fr 3fr"
      width="100%"
    >
      <VStack alignItems="flex-start" spacing="space-0">
        <SunshineCard.Title>
          {locales.type[request.type].name}
        </SunshineCard.Title>
        <SunshineCard.Text>
          {request.status === TeamMembersRequestStatus.Pending
            ? (locales.formatString(locales.subtitleWithDate, {
                date: formatDate(new Date(request.createdAt), 'PP'),
                name: request.requester
                  ? `${request.requester.firstName} ${request.requester.lastName}`
                  : '',
              }) as string)
            : (locales.formatString(locales.subtitle, {
                name: request.requester
                  ? `${request.requester.firstName} ${request.requester.lastName}`
                  : '',
              }) as string)}
        </SunshineCard.Text>
      </VStack>
      <TeamMemberRequestStatusBadge
        from="account-holder"
        justifySelf="center"
        request={request}
      />
      {isVirtualBudgetCardRequest(request) ? (
        <Text
          fontWeight={500}
          sx={{
            fontFeatureSettings: 'tnum',
            fontVariantNumeric: 'tabular-nums',
          }}
          textAlign="right"
          variant="primary"
        >
          {formatAmount({
            forcePlusSign: false,
            value:
              request.virtualBudgetCardOrderReviewerPayload?.limitPaymentAll ??
              request.virtualBudgetCardOrderRequesterPayload.limitPaymentAll,
            withNoMinorUnitDigits: true,
          })}
        </Text>
      ) : null}
    </SimpleGrid>
  </SunshineCard>
);

export default PaymentCardRequestRow;
